// src/shared/components/ui/banner.tsx

import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/shared/utils/cn'

type BannerTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

interface BannerProps extends HTMLAttributes<HTMLDivElement> {
  tone?: BannerTone
  title?: string
  icon?: ReactNode
  actions?: ReactNode
}

const toneStyles: Record<BannerTone, string> = {
  neutral: 'bg-[var(--panel-subtle)] border-[var(--panel-border)] text-[var(--color-text-soft)]',
  success:
    'bg-[var(--color-success-bg)] border-[var(--color-success-border)] text-[var(--color-success-fg)]',
  warning:
    'bg-[var(--color-warning-bg)] border-[var(--color-warning-border)] text-[var(--color-warning-fg)]',
  danger:
    'bg-[var(--color-danger-bg)] border-[var(--color-danger-border)] text-[var(--color-danger-fg)]',
  info: 'bg-[var(--color-info-bg)] border-[var(--color-info-border)] text-[var(--color-info-fg)]',
}

const defaultIconMap: Record<BannerTone, ReactNode> = {
  neutral: <Info className="h-4 w-4" />,
  success: <CheckCircle2 className="h-4 w-4" />,
  warning: <TriangleAlert className="h-4 w-4" />,
  danger: <AlertCircle className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
}

export function Banner({
  className,
  tone = 'neutral',
  title,
  icon,
  actions,
  children,
  ...props
}: BannerProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm',
        toneStyles[tone],
        className,
      )}
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      {...props}
    >
      <span className="mt-0.5 shrink-0">{icon ?? defaultIconMap[tone]}</span>
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium text-[var(--color-text)]">{title}</p> : null}
        {children ? <div className={cn('text-current', title && 'mt-1')}>{children}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
    </div>
  )
}
